import {
  PhysicalKind,
  is_data_type_numeric,
  is_data_type_temporal,
  supports_extract_unique_tuple_member,
  supports_extract_unique_values,
} from "target_wasm";
import { canDataTypeBeLinked } from "@/components/canvas/detailsSide/TableLinks/helpers";
import type { DisplayTarget } from "@/actions/catalog";

/** The one answer for the `⋮` menu and the action bar; a flag a surface does
 *  not pass reads as "no". Removing a column from a VIEW is `columnGuards.ts`. */
export interface ColumnGateFacts {
  dataType: PhysicalKind;
  parentTableId?: string;
  isColumnEmpty?: boolean;
  isLockedType?: boolean;
  isComputed?: boolean;
  hasDescription?: boolean;
  isHiddenColumn?: boolean;
  isDeletedColumn?: boolean;
  schemaEditable?: boolean;
  /** Not derivable from the column: both come from `clientApp` per table. */
  isLinked?: boolean;
  isFiltered?: boolean;
  isDragging?: boolean;
  /** The host's own reason to refuse a delete — an active commit. */
  deleteDisabled?: boolean;
  canEditModel?: boolean;
}

type Host = Pick<ColumnGateFacts, "canEditModel" | "schemaEditable">;

/** An editor, on a table whose schema is the model's own: every edit asks it,
 *  so no host ANDs the role in on its own. */
const editable = (f: Host) => !!f.canEditModel && !!f.schemaEditable;
const live = (f: ColumnGateFacts) => !f.isHiddenColumn && !f.isDeletedColumn;
const inTable = (f: ColumnGateFacts) => live(f) && !!f.parentTableId;
const editableInTable = (f: ColumnGateFacts) => editable(f) && inTable(f);

export const canSetDataType = (f: ColumnGateFacts) =>
  editableInTable(f) && !!f.isColumnEmpty && f.dataType === PhysicalKind.ND;

/** Narrower than "every Timestamp": only the SECONDS timestamp has a display
 *  view, and Decimal is not a formattable number. */
export function displayFormatTarget(f: ColumnGateFacts): DisplayTarget | null {
  if (!inTable(f)) return null;
  if (
    f.dataType === PhysicalKind.TimestampUTCSeconds ||
    f.dataType === PhysicalKind.Duration
  )
    return "display";
  if (
    f.dataType === PhysicalKind.F64 ||
    f.dataType === PhysicalKind.F32 ||
    f.dataType === PhysicalKind.I32 ||
    f.dataType === PhysicalKind.I64 ||
    f.dataType === PhysicalKind.U32 ||
    f.dataType === PhysicalKind.U64
  )
    return "format";
  if (f.dataType === PhysicalKind.String) return "stringFormat";
  return null;
}

/** `set_locked_dtype(false)` refuses an Attachment column: its kind is the lock. */
export const kindLocksType = (k: PhysicalKind): boolean =>
  k === PhysicalKind.Attachment;

/** The data-type lock toggle: a live typed column of a kind a user can
 *  unlock. Typed is `dataType !== ND`, not "has values". */
export const canToggleLock = (
  f: Pick<ColumnGateFacts, "dataType" | "isDeletedColumn"> & Host,
): boolean =>
  editable(f) &&
  !f.isDeletedColumn &&
  f.dataType !== PhysicalKind.ND &&
  !kindLocksType(f.dataType);

export const canManageCategories = (f: ColumnGateFacts) =>
  editableInTable(f) && f.dataType === PhysicalKind.Categorical;

export const canSetUnit = (f: ColumnGateFacts) =>
  canChangeSchema(f) && f.dataType !== PhysicalKind.Categorical;

export const canEditIntents = (f: ColumnGateFacts) =>
  editableInTable(f) && !f.isComputed;

/** `deleteDisabled` is the staged commit, which refuses any schema change. */
export const canChangeSchema = (f: ColumnGateFacts) =>
  editableInTable(f) && !f.deleteDisabled;

/** Either description case. The menu offers the "add" one only; a surface
 *  with a single description slot covers both. */
export const canOpenDescription = editableInTable;

/** The "add" case only: once a description exists the row badge is the way in. */
export const canAddDescription = (f: ColumnGateFacts) =>
  canOpenDescription(f) && !f.hasDescription;

export const canCastColumn = (f: ColumnGateFacts) =>
  editable(f) &&
  !f.isDragging &&
  live(f) &&
  !f.isLockedType &&
  !f.isColumnEmpty;

export const canExtractUnique = (f: ColumnGateFacts) =>
  editableInTable(f) &&
  supports_extract_unique_values(f.dataType) &&
  !f.isColumnEmpty;

/** The tuple form of Extract unique: two or more live, non-empty columns. */
export const canExtractUniqueTuples = (
  columns: readonly (
    | { dataType: PhysicalKind; isColumnEmpty?: boolean; isDeleted?: boolean }
    | undefined
  )[],
  host: Host,
): boolean =>
  editable(host) &&
  columns.length >= 2 &&
  columns.every(
    (c) =>
      !!c &&
      !c.isDeleted &&
      !c.isColumnEmpty &&
      supports_extract_unique_tuple_member(c.dataType),
  );

const textWithValues = (f: ColumnGateFacts) =>
  editableInTable(f) && f.dataType === PhysicalKind.String && !f.isColumnEmpty;

export const canSearchReplace = textWithValues;
export const canSplit = textWithValues;
export const canMapToReference = textWithValues;

export const canLink = (f: ColumnGateFacts) =>
  !!f.canEditModel && inTable(f) && canDataTypeBeLinked(f.dataType);

/** The column ⋮ is an editor's, so Statistics in it is too. */
export const canShowStats = (f: ColumnGateFacts) =>
  !!f.canEditModel &&
  (is_data_type_numeric(f.dataType) ||
    is_data_type_temporal(f.dataType) ||
    f.dataType === PhysicalKind.Duration) &&
  inTable(f);

/** A linked or filtered column refuses deletion, as does one mid-commit. A
 *  hidden one still deletes: an open product question, not a gate to tighten. */
export const canDelete = (f: ColumnGateFacts) =>
  editable(f) &&
  !f.isDeletedColumn &&
  !(f.deleteDisabled || f.isLinked || f.isFiltered);

export const canRestore = (f: ColumnGateFacts) =>
  editable(f) && !!f.isDeletedColumn;

/** Irreversible, so it never widens: only an already soft-deleted column. */
export const canHardDelete = (f: ColumnGateFacts) =>
  editable(f) && !!f.isDeletedColumn;
